import {
  alpha,
  Breakpoint,
  createTheme,
  ThemeOptions,
} from "@mui/material/styles";
import { buttonClasses } from "@mui/material";
import { darken, lighten } from "@mui/system";
import { resolvedTheme } from "./tailwind.theme";
import { twMerge } from "tailwind-merge";
import clsx, { ClassValue } from "clsx";

const theme = resolvedTheme;

export const classes = (...inputs: ClassValue[]): string =>
  twMerge(clsx(inputs));

export const GRAPH_COLORS = [
  "#a34316",
  "#1676a3",
  "#a31630",
  "#a38916",
  "#76a316",
  "#16a343",
];

export const CHIP_COLORS = [
  {
    bg: theme.colors.carbon.orange,
    text: theme.colors.white,
  },

  { bg: "#F9DB6D", text: theme.colors.black },
  { bg: theme.colors.carbon.blue, text: theme.colors.black },
  {
    bg: "#1C7C54",
    text: theme.colors.white,
  },
  {
    bg: "#B1EDE8",
    text: theme.colors.black,
  },
  {
    bg: "#9C528B",
    text: theme.colors.white,
  },
];

export const STALE_CHIP_COLOR = {
  bg: theme.colors.carbon.gray.light,
  text: theme.colors.black,
};

const TYPOGRAPHY = {
  fontSize: 13.5,
  fontFamily: ["Poppins", "sans-serif"].join(", "),
  h1: {
    fontFamily: ["Russo One", "Poppins", "sans-serif"].join(", "),
    fontWeight: "normal" as const,
    textTransform: "uppercase" as const,
  },
  h2: {
    fontFamily: ["Russo One", "Poppins", "sans-serif"].join(", "),
    fontWeight: "normal" as const,
    textTransform: "uppercase" as const,
  },
  h3: {
    fontFamily: ["Russo One", "Poppins", "sans-serif"].join(", "),
    fontWeight: "normal" as const,
    textTransform: "uppercase" as const,
  },
  h4: {
    fontFamily: ["Russo One", "Poppins", "sans-serif"].join(", "),
    fontWeight: "normal" as const,
    textTransform: "uppercase" as const,
  },
  h5: {
    fontFamily: ["Poppins", "sans-serif"].join(", "),
    fontWeight: "bold" as const,
  },
  h6: {
    fontFamily: ["Poppins", "sans-serif"].join(", "),
    fontWeight: "bold" as const,
  },
};

/** Pastel band fills for charts; cycles when band count exceeds length. */
const LIGHT_CHART_BAND_COLORS = [
  theme.colors.blue["100"],
  theme.colors.green["100"],
  theme.colors.yellow["200"],
  theme.colors.orange["200"],
] as const;

const DARK_CHART_BAND_COLORS = [
  alpha(theme.colors.blue["800"], 0.28),
  alpha(theme.colors.green["800"], 0.28),
  alpha(theme.colors.yellow["800"], 0.1),
  alpha(theme.colors.blue["700"], 0.45),
] as const;

const LIGHT_CHART_BAND_STROKE_COLORS = [
  theme.colors.blue["500"],
  theme.colors.green["500"],
  theme.colors.yellow["600"],
  theme.colors.orange["700"],
] as const;

const DARK_CHART_BAND_STROKE_COLORS = [
  theme.colors.blue["300"],
  theme.colors.green["300"],
  theme.colors.yellow["400"],
  theme.colors.orange["400"],
] as const;

const LIGHT_PALETTE = {
  primary: {
    light: theme.colors.orange["300"],
    main: theme.colors.orange["500"],
    dark: theme.colors.orange["800"],
  },
  secondary: {
    light: theme.colors.blue["300"],
    main: theme.colors.blue["500"],
    dark: theme.colors.blue["800"],
  },
  warning: {
    light: theme.colors.yellow["300"],
    main: theme.colors.yellow["600"],
    dark: theme.colors.yellow["800"],
  },
  info: {
    light: theme.colors.blue["300"],
    main: theme.colors.blue["500"],
    dark: theme.colors.blue["800"],
  },
  background: {
    paper: theme.colors.white,
    section: darken(theme.colors.carbon.gray.lightest, 0.1),
    default: theme.colors.carbon.gray.lightest,
  },
  DataGrid: {
    bg: theme.colors.carbon.gray.lightest,
    headerBg: theme.colors.white,
    pinnedBg: theme.colors.carbon.gray.lightest,
  },
  text: {
    primary: theme.colors.darken["800"],
    secondary: theme.colors.darken["600"],
    disabled: theme.colors.darken["200"],
  },
  chart: {
    bandColors: [...LIGHT_CHART_BAND_COLORS],
    bandLabelColor: theme.colors.darken["500"],
    bandStrokeColors: [...LIGHT_CHART_BAND_STROKE_COLORS],
    comparisonColor: theme.colors.error["200"],
    gridColor: theme.colors.darken["200"],
    plotBackground: "transparent",
    tickColor: theme.colors.darken["400"],
  },
  atkActions: {
    cardBackground: darken(theme.colors.carbon.gray.lightest, 0.05),
    iconBackground: alpha(theme.colors.orange["500"], 0.08),
    iconForeground: theme.colors.orange["500"],
  },
};

const DARK_PALETTE = {
  primary: {
    light: theme.colors.orange["300"],
    main: theme.colors.orange["500"],
    dark: theme.colors.orange["800"],
  },
  secondary: {
    light: theme.colors.blue["300"],
    main: theme.colors.blue["500"],
    dark: theme.colors.blue["800"],
  },
  warning: {
    light: theme.colors.yellow["300"],
    main: theme.colors.yellow["500"],
    dark: theme.colors.yellow["800"],
  },
  info: {
    light: theme.colors.blue["300"],
    main: theme.colors.blue["500"],
    dark: theme.colors.blue["800"],
  },
  background: {
    paper: theme.colors.carbon.gray.medium,
    section: lighten(theme.colors.carbon.gray.dark, 0.15),
    default: theme.colors.carbon.gray.dark,
  },
  DataGrid: {
    bg: theme.colors.carbon.gray.dark,
    headerBg: theme.colors.carbon.gray.medium,
    pinnedBg: theme.colors.carbon.gray.dark,
  },
  text: {
    primary: theme.colors.lighten["900"],
    secondary: theme.colors.lighten["600"],
    disabled: theme.colors.lighten["200"],
  },
  chart: {
    bandColors: [...DARK_CHART_BAND_COLORS],
    bandLabelColor: theme.colors.lighten["500"],
    bandStrokeColors: [...DARK_CHART_BAND_STROKE_COLORS],
    comparisonColor: theme.colors.error["400"],
    gridColor: theme.colors.lighten["200"],
    plotBackground: theme.colors.carbon.gray.semidark,
    tickColor: theme.colors.lighten["400"],
  },
  atkActions: {
    cardBackground: lighten(theme.colors.carbon.gray.medium, 0.15),
    iconBackground: alpha(theme.colors.lighten["900"], 0.2),
    iconForeground: theme.colors.lighten["900"],
  },
};

const BREAKPOINTS = {
  xs: 0,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
};

export const getCarbonThemeOptions = (
  mode: "light" | "dark" = "dark"
): ThemeOptions => ({
  typography: TYPOGRAPHY,
  palette: {
    mode,
    ...(mode === "dark" ? DARK_PALETTE : LIGHT_PALETTE),
  },
  shape: {
    borderRadius: 2,
  },
  breakpoints: {
    values: BREAKPOINTS,
  },
  components: {
    MuiTooltip: {
      defaultProps: {
        slotProps: {
          tooltip: { className: "bg-darken-800" },
          arrow: { className: "text-darken-800" },
        },
      },
    },
    MuiFormControl: {
      defaultProps: {
        className: "mt-2",
      },
    },
    MuiPaper: {
      variants: [
        {
          props: { variant: "section" },
          style: ({ theme: muiTheme }) => ({
            backgroundColor: muiTheme.palette.background.section,
          }),
        },
        {
          props: { variant: "atk-action-card" },
          style: ({ theme: muiTheme }) => ({
            backgroundColor: muiTheme.palette.atkActions.cardBackground,
          }),
        },
      ],
    },
    MuiButton: {
      variants: [
        {
          props: { variant: "atk-new-action-selection" },
          style: ({ theme: muiTheme }) => ({
            padding: "5px 15px",
            border: "1px solid currentColor",
            borderColor: `var(--variant-outlinedBorder, currentColor)`,
            backgroundColor: `var(--variant-outlinedBg)`,
            color: `var(--variant-outlinedColor)`,
            [`&.${buttonClasses.disabled}`]: {
              border: `1px solid ${muiTheme.palette.action.disabledBackground}`,
            },
            textTransform: "none",
            borderRadius: "0.75rem", // matches `rounded-xl`
          }),
        },
      ],
    },
    MuiStepButton: {
      styleOverrides: {
        root: {
          paddingTop: 0,
          paddingBottom: 0,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          variants: [
            {
              props: { variant: "carbon-tab" },
              style: {
                // maintain the light mode color at all times since nav tabs always have a red background
                color: "rgba(255, 255, 255, .6)",
              },
            },
          ],
        },
      },
    },
  },
});

export const carbon = createTheme(getCarbonThemeOptions("dark"));

/**
 * There is also a good reason to disable the flag, which is if you need to use
 * the :nth-child(an + b of <selector>) pseudo-class for which no :nth-of-type()
 * equivalent exists
 */
export const FLAG_DISABLE_SSR_WARNING =
  "/* emotion-disable-server-rendering-unsafe-selector-warning-please-do-not-use-this-the-warning-exists-for-a-reason */";

export type Breakpoints = Partial<Record<Breakpoint, number>>;

export const TRACTOR_COLORS = [
  "#FF0000", // Red
  "#00FF00", // Green
  "#0366fc", // Blue
  "#FFFF00", // Yellow
  "#00FFFF", // Cyan
  "#F000F0", // Magenta
  "#F58231", // Orange
  "#FFA0BB", // Pink
  "#911EB4", // Purple
  "#9A6324", // Brown
];

export const CENTER_PIVOT_FRESH = "#00AA00";
